import { useCallback, useState } from 'react'
import { IconButton } from '@features/shared/components/IconButton'
import Popover from '@features/shared/components/Popover'
import { COMPACT_POPOVER_CONTENT_CLASS } from '@features/shared/components/compactDropdownStyles'
import { ConnectedShareIcon, SpinnerIcon } from '@features/shared/icons/icons'
import { useDescribeSharedPage } from '../hooks/useDescribeSharedPage'
import { useShareActions } from '../hooks/useShareActions'
import { pageSharePayload, shareUrl } from '../logic/sharePayload'
import ShareMenu from './ShareMenu'

/**
 * Shares the page that's open, from the app's top bar beside Support and
 * AI Assistant, so every page can be shared. The link is the page's address as
 * it stands (a grid's names its view); side panels aren't part of it, since
 * they're the viewer's own workspace. A grid also shares just its view, from
 * its Export menu's Link row (ExportLinkRow).
 */
/** The share glyph's size; the header places the line before it by it. */
export const SHARE_ICON_SIZE = 17

export default function PageShareButton({ className }: { className: (open: boolean) => string }) {
  const [open, setOpen] = useState(false)
  const describePage = useDescribeSharedPage()

  const prepare = useCallback(async () => {
    const { pathname, search } = window.location
    return pageSharePayload(shareUrl(`${pathname}${search}`), await describePage(pathname))
  }, [describePage])
  const actions = useShareActions(prepare, 'The page link is ready to share.')

  return (
    <Popover
      open={open}
      onOpenChange={actions.isPreparing ? () => {} : setOpen}
      align="right"
      showCaret={false}
      wrapperClassName="inline-flex shrink-0"
      contentClassName={COMPACT_POPOVER_CONTENT_CLASS}
      trigger={
        <IconButton
          label="Share page"
          aria-haspopup="menu"
          aria-expanded={open}
          disabled={actions.isPreparing}
          disabledReason={actions.isPreparing ? 'Preparing link...' : undefined}
          wrapperClassName="shrink-0"
          className={className(open)}
        >
          {actions.isPreparing ? (
            <SpinnerIcon size={18} aria-hidden="true" />
          ) : (
            <ConnectedShareIcon size={SHARE_ICON_SIZE} aria-hidden="true" />
          )}
        </IconButton>
      }
    >
      <ShareMenu noun="page" actions={actions} onClose={() => setOpen(false)} />
    </Popover>
  )
}
